import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getSummary } from "../../api/process";
import WidgetCard from "./WidgetCard";
import { Settings } from "lucide-react";
import { createPortal } from "react-dom";
import { updateProjectSafeDate } from "../../api/project";

export default function MobileDDayCounter() {
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const { id: projectId } = useParams();
  const [safeStartDate, setSafeStartDate] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarPos, setCalendarPos] = useState({ top: 0, left: 0 });
  const settingsRef = useRef(null);
  const calendarRef = useRef(null);

  const today = new Date();
  const safeStart = new Date(safeStartDate);

  // 하루 단위 차이 계산
  const safeDays = Math.max(
    Math.floor((today - safeStart) / (1000 * 60 * 60 * 24)),
    0
  );

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const data = await getSummary(projectId);
        console.log(data);
        setStartDate(data.period_start);
        setEndDate(data.period_finish);
        setSafeStartDate(data.safe_date || "");
      } catch (error) {
        console.error("프로젝트 요약 데이터 불러오기 실패:", error);
      }
    };
    fetchSummary();
  }, [projectId]);

  useEffect(() => {
    if (!showCalendar) return;

    function handleClickOutside(e) {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setShowCalendar(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCalendar]);

  if (!startDate || !endDate) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 w-full text-center text-slate-500">
        프로젝트 기간 정보를 불러오는 중입니다...
      </div>
    );
  }

  const start = new Date(startDate);
  const end = new Date(endDate);
  const totalDays = Math.max(
    Math.floor((end - start) / (1000 * 60 * 60 * 24)),
    1
  );
  const passedDays = Math.max(
    Math.floor((today - start) / (1000 * 60 * 60 * 24)),
    0
  );
  const remainingDays = Math.max(totalDays - passedDays, 0);

  return (
    <WidgetCard title="프로젝트 진행 현황">
      <div className="flex flex-row items-start justify-center gap-3">
        {/* D-Day 영역 */}
        <div className="text-center flex-shrink-0">
          <p className="text-[9px] text-slate-500 mb-0.5">
            Today {today.getFullYear()}.{String(today.getMonth() + 1).padStart(2, "0")}.{String(today.getDate()).padStart(2, "0")}
          </p>
          <p className="text-2xl font-extrabold text-blue-700 leading-none mb-0.5">
            D-{remainingDays}
          </p>
          <p className="text-[9px] text-slate-400 leading-tight">
            총 {totalDays}일 중 {passedDays}일 경과
          </p>
          <p className="text-[8px] text-slate-400 leading-tight">
            {startDate} ~ {endDate}
          </p>
        </div>

        {/* 무사고 영역 */}
        {safeStartDate && (
          <div className="text-center flex-shrink-0">
            <p className="text-[9px] text-slate-500 mb-0.5 invisible">
              placeholder
            </p>
            <p className="text-2xl font-extrabold text-green-600 leading-none mb-0.5">
              무사고 {safeDays}일
            </p>
            <div className="flex items-center justify-center gap-1 text-[8px] text-slate-400">
              <span className="leading-tight">({safeStartDate.replace(/-/g, ".")} ~)</span>
              <button
                ref={settingsRef}
                onClick={() => {
                  if (settingsRef.current) {
                    const rect = settingsRef.current.getBoundingClientRect();
                    setCalendarPos({
                      top: rect.bottom + 6,
                      left: rect.left - 20,
                    });
                  }
                  setShowCalendar(true);
                }}
                className="p-0.5 hover:bg-slate-100 rounded"
              >
                <Settings size={9} className="text-slate-400" />
              </button>

              {showCalendar &&
                createPortal(
                  <div
                    ref={calendarRef}
                    className="absolute bg-white border rounded-md shadow p-3 w-40 z-[9999]"
                    style={{
                      position: "absolute",
                      top: calendarPos.top,
                      left: calendarPos.left,
                    }}
                  >
                    <label className="text-xs text-slate-600">
                      시작일 설정
                    </label>
                    <input
                      type="date"
                      value={safeStartDate}
                      onChange={async (e) => {
                        const newDate = e.target.value;
                        await updateProjectSafeDate(newDate);
                        setSafeStartDate(newDate);
                        setShowCalendar(false);
                      }}
                      className="border p-1 rounded mt-1 w-full text-xs"
                    />
                  </div>,
                  document.body
                )}
            </div>
          </div>
        )}
      </div>
    </WidgetCard>
  );
}
